import { useCallback } from 'react';
import { CanvasElementType, CanvasMode, CanvasGroup, CanvasNode } from '../canvas-types';
import { RfNodeGroup, RfNodeGroupLabel, RfNodeGroupOverlay } from '../canvas-types/nodeTypesData';
import { useSweepCanvasPropsCtx } from '../internal-context/SweepCanvasPropsCtx';
import { calculateGroupLabelPosition } from '../helpers/calculateGroupLabelPosition';
import { calculateGroupDimensions } from '../calculateGroupDimensions';
import { useGetShowButtonsOnHighlight } from '../effects/useContextZoomEffect';
import { useSweepCanvasState } from '../internal-context/CanvasStateContext';
import { useDynamicNodeDimensions } from './useDynamicNodeDimensions';
import { adjustNodePositionForSection } from '../canvas-element-tool/helper';
import { convertGridPositionToXYPosition } from '../helpers/gridPositioningUtils';

export const useGroupNodesFactory = () => {
  const {
    hideGroupInfo,
    canvasMode = CanvasMode.DEFAULT,
    onPluginClick,
    showGroupOverlays,
    readonly,
    onNodeClick,
    onPillClick,
    onLabelClick,
    onConnectClick,
    simpleHighlightedEntities,
  } = useSweepCanvasPropsCtx();
  const { getDynamicNodeDimensions } = useDynamicNodeDimensions();

  const showButtonsOnHighlight = useGetShowButtonsOnHighlight();
  const { zoomTransformSuffix } = useSweepCanvasState();

  const calculateGroupNodes = useCallback(
    ({
      disableGroups,
      sweepNodes,
      sweepGroups,
    }: {
      disableGroups?: boolean;
      sweepNodes: CanvasNode[];
      sweepGroups: CanvasGroup[];
    }) => {
      if (disableGroups) {
        return {
          groupNodes: [],
          groupLabelNodes: [],
          groupOverlayNodes: [],
        };
      }

      const ghostGroups = new Set(
        sweepNodes
          .filter((node) => node.type === CanvasElementType.GHOST_NODE)
          .map((node) => node.parentId),
      );

      const groupNodes: RfNodeGroup[] = [];
      const groupLabelNodes: RfNodeGroupLabel[] = [];
      const groupOverlayNodes: RfNodeGroupOverlay[] = [];

      sweepGroups.forEach((sweepGroup) => {
        const { groupBoundingBox, groupNodeBoundaries } = calculateGroupDimensions({
          nodeWithCanvasPositions: sweepNodes.map((node) => ({
            id: node.id,
            position: node.position,
            parentId: node.parentId,
          })),
          groupId: sweepGroup.id,
          isPreview: canvasMode !== CanvasMode.DEFAULT,
        });

        const groupPosition = convertGridPositionToXYPosition(sweepGroup.position, {
          withPaddingByNodeType: CanvasElementType.GROUP,
        });
        sweepGroup.sectionId && adjustNodePositionForSection(groupPosition);

        const preview = canvasMode !== CanvasMode.DEFAULT;

        if (showGroupOverlays) {
          groupOverlayNodes.push({
            id: `overlay-${sweepGroup.id}`,
            data: {
              boundingBox: groupBoundingBox,
              nodeBoundaries: groupNodeBoundaries,
              onClick: onNodeClick,
              groupId: sweepGroup.id,
            },
            position: groupPosition,
            draggable: false,
            selectable: false,
            type: CanvasElementType.GROUP_OVERLAY,
            hidden: false,
            width: 1,
            height: 1,
            zIndex: 9999,
          });
        }

        const newGroupNode: RfNodeGroup = {
          id: sweepGroup.id,
          data: {
            nodeBoundaries: groupNodeBoundaries,
            boundingBox: groupBoundingBox,
            objectType: sweepGroup.objectType,
            hideGroupInfo,
            onClick: onNodeClick,
            isGhost: ghostGroups.has(sweepGroup.id),
            showEditButton: sweepGroup.showEditButton,
            groupId: sweepGroup.id,
            simpleHighlighted: simpleHighlightedEntities?.includes(sweepGroup.id),
            topTag: sweepGroup.topTag,
            zoomTransformSuffix,
          },
          position: groupPosition,
          draggable: true,
          selectable: true,
          type: CanvasElementType.GROUP,
          hidden: false,
          width: groupBoundingBox.width,
          height: groupBoundingBox.height,
          parentId: sweepGroup.sectionId,
          expandParent: true,
        };

        groupNodes.push(newGroupNode);

        if (!preview) {
          const labelId = `label-${sweepGroup.id}`;
          const { width, height } = getDynamicNodeDimensions(labelId);

          const labelPosition = calculateGroupLabelPosition(groupPosition, groupBoundingBox);
          groupLabelNodes.push({
            id: labelId,
            data: {
              label: sweepGroup.name,
              boundingBox: groupBoundingBox,
              objectType: sweepGroup.objectType,
              objectLabel: sweepGroup.objectLabel,
              readonly,
              hideGroupInfo,
              groupId: sweepGroup.id,
              onLabelClick,
              onPillClick,
              labelPlugins:
                sweepGroup.plugins?.map((pluginType) => ({
                  type: pluginType,
                  onClick: (event) =>
                    onPluginClick?.({
                      parentId: sweepGroup.id,
                      pluginId: pluginType,
                      objectType: sweepGroup.objectType,
                      event,
                    }),
                })) || [],

              pills: sweepGroup.pills,
              startingIcon: sweepGroup.startingIcon,
              description: sweepGroup.description,
              objectLabelLogoDevBrand: sweepGroup.objectLabelLogoDevBrand,
              onConnectClick,
              showButtonsOnHighlight,
              simpleHighlighted: simpleHighlightedEntities?.includes(sweepGroup.id),
              zoomTransformSuffix,
              status: sweepGroup.status,
              moreButton: sweepGroup.moreButton,
            },
            position: labelPosition,
            draggable: true,
            selectable: false,
            type: CanvasElementType.GROUP_LABEL,
            zIndex: 1001,
            height,
            width,
            parentId: sweepGroup.sectionId,
          });
        }
      });

      return {
        groupNodes,
        groupLabelNodes,
        groupOverlayNodes,
      };
    },
    [
      canvasMode,
      hideGroupInfo,
      onConnectClick,
      onLabelClick,
      onNodeClick,
      onPillClick,
      onPluginClick,
      readonly,
      showButtonsOnHighlight,
      showGroupOverlays,
      simpleHighlightedEntities,
      zoomTransformSuffix,
      getDynamicNodeDimensions,
    ],
  );

  return { calculateGroupNodes };
};
